import React from "react";

import { recordVersionLabel } from "@/components/shared/revision-labels";
import { Alert, Badge, countLabel, Heading, KeyValue, Text, Time } from "@/components/ui";
import type { ProgrammeGovernanceSummary } from "@/platform";

export interface ApprovalCandidateProps {
  approvalCandidate: ProgrammeGovernanceSummary["approvalCandidates"][number] | undefined;
  /** A user's name, from their id. */
  nameFor: (userId: string) => string;
}

export function ApprovalCandidate({
  approvalCandidate,
  nameFor,
}: ApprovalCandidateProps): React.ReactElement | null {
  if (!approvalCandidate) {
    return null;
  }
  const retained = approvalCandidate.readinessOverrideBlockers?.length ?? 0;

  return (
    <section className="mt-4" data-testid="approval-candidate">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Submitted approval candidate
          </Heading>
          <Text tone="muted" className="mt-1">
            Locked content snapshot submitted by {nameFor(approvalCandidate.submittedBy)}
            {" on "}
            <Time value={approvalCandidate.submittedAt} />.
          </Text>
        </div>
        <Badge variant="outline" data-testid="approval-candidate-revision">
          {recordVersionLabel(approvalCandidate.programmeRevision)}
        </Badge>
      </div>
      <KeyValue
        columns={3}
        className="mt-3"
        entries={[
          {
            label: "Submitted readiness",
            value: `${approvalCandidate.readiness.state}${
              approvalCandidate.readiness.blockers.length
                ? ` (${countLabel(approvalCandidate.readiness.blockers.length, "blocker")})`
                : ""
            }`,
            "data-testid": "approval-candidate-readiness",
          },
          {
            label: "Assigned reviewers",
            value: approvalCandidate.reviewerUserIds.length
              ? approvalCandidate.reviewerUserIds.map(nameFor).join(", ")
              : "Unassigned",
            "data-testid": "approval-candidate-reviewers",
          },
          {
            label: "Client Admin approvers",
            value: approvalCandidate.approverUserIds.length
              ? approvalCandidate.approverUserIds.map(nameFor).join(", ")
              : "Unassigned",
            "data-testid": "approval-candidate-approvers",
          },
        ]}
      />
      {approvalCandidate.readinessOverrideDecisionIds?.length ? (
        <Alert
          tone="warning"
          title="Submitted under a Client Admin readiness override"
          className="mt-4"
          data-testid="approval-candidate-readiness-override"
        >
          <Text as="div" className="mt-1">
            {countLabel(retained, "unresolved requirement")} {retained === 1 ? "was" : "were"} kept
            with this candidate.
          </Text>
          <Text as="div" size="sm" className="mt-2 break-all font-mono">
            Reference: {approvalCandidate.readinessOverrideDecisionIds.join(", ")}
          </Text>
        </Alert>
      ) : null}
    </section>
  );
}
